import type { ServedTier } from "../segmenting/dictionary.js";
import { readerSignedIn } from "./reader-session.js";
import { TierLoading } from "./tier-loading.js";
import { TierOffer } from "./tier-offer.js";
import { ToolSettings } from "./tool-settings.js";
import { readUrlState, type WritableState } from "./url-state.js";

/**
 * What a dictionary-backed tool opens in, and what it is in now.
 *
 * Shared by the hanzi tool and the segmenter, which differ in what they do
 * with a text and agree on everything around it: both read the same three
 * things out of the address bar, both offer the same two dictionary tiers, and
 * both put the same state back. Written once here so that a change to how a
 * link is read reaches both rather than one.
 */

/**
 * The parts of a page that carry a tool's state.
 *
 * Narrower than either tool's own elements, and deliberately so: a tool's
 * output is its own business, and nothing in this module has any use for it.
 */
export interface TieredElements {
  readonly input: HTMLTextAreaElement;

  /** Where loading, and failing to load, is reported. */
  readonly status: HTMLElement;

  /** The settings panel the tools share. */
  readonly controls: HTMLElement;

  readonly upgrade: HTMLButtonElement | undefined;

  /**
   * The block holding the upgrade button and the note explaining it.
   *
   * Separate from the button because it is what gets hidden: the note is only
   * meaningful while the offer stands. Optional like the button, since a
   * template may offer no upgrade at all.
   */
  readonly upgradePanel: HTMLElement | undefined;

  /**
   * The other direction: back to the smaller dictionary, and stop keeping the
   * bigger one.
   *
   * Only meaningful on a page that offers the upgrade, and optional for the
   * same reason it is.
   */
  readonly revert: HTMLButtonElement | undefined;
  readonly revertPanel: HTMLElement | undefined;
}

/**
 * The tool's state: what it opens in, and what it is in now.
 *
 * Three sources, in order: the link somebody followed, what the last visit
 * stored on this device, and the defaults. A link says what it is for and
 * would be a strange thing to open and find showing something else, but it
 * only speaks for what it names, so anything it leaves out still comes from
 * this device.
 */
export interface Startup {
  readonly settings: ToolSettings;
  readonly tiers: TierLoading;

  /**
   * The dictionary a link asked for, or nothing where it did not ask.
   *
   * Undefined rather than the default, because the two are answered
   * differently now: a link naming a tier is obeyed, and a link naming none
   * leaves the tool free to open on whatever the device already has. Collapsing
   * them here would make `?dictionary=standard` unable to mean what it says.
   */
  readonly tier: ServedTier | undefined;
}

/**
 * Read the address bar, fill the input from it, and show the settings.
 */
export function startingState(
  elements: TieredElements,
  dictionaryUrl: string,
  onSettingsChanged: () => void,
): Startup {
  const fromUrl = readUrlState(location.search);

  if (fromUrl.text !== undefined) {
    elements.input.value = fromUrl.text;
  }

  return {
    tiers: new TierLoading({
      dictionaryUrl,
      status: elements.status,
      upgrade: new TierOffer({
        button: elements.upgrade,
        panel: elements.upgradePanel,
      }),
      revert: new TierOffer({
        button: elements.revert,
        panel: elements.revertPanel,
      }),
      signedIn: readerSignedIn(document),
    }),
    settings: new ToolSettings(
      elements.controls,
      fromUrl.options,
      onSettingsChanged,
    ),
    tier: fromUrl.tier,
  };
}

/**
 * What the address bar says, and what Copy link copies.
 *
 * The text, the settings and the dictionary — everything somebody would want
 * back if they bookmarked this or sent it to a colleague.
 */
export function currentState(
  elements: TieredElements,
  settings: ToolSettings,
  tiers: TierLoading,
): WritableState {
  return {
    text: elements.input.value,
    options: settings.options,
    tier: tiers.tier,
  };
}
